import React from "react";
import { Table, Input, Button, Space, notification, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";
import useSWR from "swr";
import "./data_supply_slip.css";
import { useAuth } from "../../../context/AuthContext";

const fetcher = (url: string) => axios.get(url).then((res) => res.data);
interface DataSupplySlipProps {
    onSelect: (record: any) => void;
}

const DataSupplySlip: React.FC<DataSupplySlipProps> = ({ onSelect }) => {
    const { userData } = useAuth();
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    interface DataType {
        id: number;
        supply_slip_no: string;
        supply_slip_date: string;
        work_order_no: string;
        work_order_date: string;
        no_polisi: string;
        warehouse: string;
    }

    interface WarehouseType { warehouse_code: string; warehouse_name: string; }

    const { data: dataSource, isLoading } = useSWR<DataType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/list-data`,
        fetcher
    );

    const { data: warehouseData } = useSWR<WarehouseType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/warehouse/list`,
        fetcher
    );

    const filteredData = React.useMemo(() => {
        if (!dataSource) return [];
        const warehouseFiltered = !userData?.warehouse_code ? dataSource : dataSource.filter((item) => item.warehouse === userData.warehouse_code);
        const uniqueData = Array.from(new Map(warehouseFiltered.map(item => [item['work_order_no'], item])).values());
        return uniqueData;
    }, [dataSource, userData]);

    const getColumnSearchProps = (dataIndex: keyof DataType) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value: any, record: any) => record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    });

    const columns: ColumnsType<DataType> = [
        { title: "Work Order No", dataIndex: "work_order_no", key: "work_order_no", sorter: (a, b) => a.work_order_no.localeCompare(b.work_order_no), ...getColumnSearchProps("work_order_no") },
        { title: "Work Order Date", dataIndex: "work_order_date", key: "work_order_date", sorter: (a, b) => a.work_order_date.localeCompare(b.work_order_date), ...getColumnSearchProps("work_order_date") },
        {
            title: "Warehouse",
            dataIndex: "warehouse",
            key: "warehouse",
            sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
            ...getColumnSearchProps("warehouse"),
            render: (warehouseCode: string) => {
                const warehouse = warehouseData?.find((w) => w.warehouse_code === warehouseCode);
                return warehouse ? warehouse.warehouse_name : warehouseCode;
            },
        },
    ];

    return (

        <div id="i3b9" style={transformStyle}>
            <div className="gjs-grid-row" id="i9mh">
                <div className="gjs-grid-column" id="i1zl">
                    <Table
                        columns={columns}
                        dataSource={filteredData}
                        loading={isLoading}
                        rowKey="id"
                        onRow={(record) => ({
                            onClick: async () => {
                                try {
                                    const checkDraftRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/check-draft/${record.work_order_no}`);
                                    if (checkDraftRes.data && checkDraftRes.data.isDraft) {
                                        Modal.warning({
                                            title: "Peringatan",
                                            content: 'Nomor Work Order sedang dalam proses "Draft" silahkan selesaikan dulu !',
                                        });
                                        return;
                                    }
                                    const [ssItems, woItems] = await Promise.all([
                                        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/items/${record.work_order_no}`),
                                        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order/items/${record.work_order_no}`)
                                    ]);
                                    const enrichedItems = woItems.data.filter((woItem: any) => String(woItem.type) === '1' && Number(woItem.supply_qty || 0) > 0 && woItem.item_code !== 'External Part').map((woItem: any) => {
                                        const ssInfo = ssItems.data.find((ss: any) => ss.item_code === woItem.item_code) || {};
                                        return {
                                            ...woItem,
                                            qty_return: "",
                                            supply_slip_no: ssInfo.supply_slip_no || "",
                                            qty: Number(woItem.supply_qty),
                                            location: ssInfo.location || ""
                                        };
                                    }).filter((item: any) => {
                                        const woItem = woItems.data.find((w: any) => w.item_code === item.item_code);
                                        return item.qty > 0 && woItem?.status !== 'R';
                                    });

                                    onSelect({ ...record, items: enrichedItems });
                                } catch (error) {
                                    notification.error({ message: 'Gagal memproses data item' });
                                }
                            },
                            style: { cursor: 'pointer' }
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataSupplySlip;